import { SensorOrSubSensorInfoByType } from '@components/cockpit/panels/monitoring/monitoring.types';
import DataSheetCard from '@components/common/layout/DataSheetCard';
import { ToastStatusEnum } from '@components/ui/toaster';
import { usePostXentaCommandMutation } from '@services/c2/c2.api';
import { createSensorToast } from '@utils/sensors/sensors.utils';
import { useIntl } from 'react-intl';

import {
  XENTA_INITIAL_AZIMUTH,
  XentaCommand,
  XentaCommandEnum,
  XentaEmissionCommand,
  XentaRotationCommand,
  XentaStopRotationCommand,
  XentaSurveillanceModeEnum,
} from '@/types/c2/c2.types';
import {
  SensorTypeEnum,
  XentaCapabilityStatusTypeEnum,
  XentaMastMotionEnum,
  XentaTransmitStateEnum,
} from '@/types/sensor/sensors';

export default function XentaCommands({
  status,
  configuration,
}: Readonly<SensorOrSubSensorInfoByType<SensorTypeEnum.XENTA>>) {
  const { formatMessage } = useIntl();
  const [postXentaCommand, { isLoading }] = usePostXentaCommandMutation();

  const xentaTransmitState =
    !!status && status.capabilities[XentaCapabilityStatusTypeEnum.TRANSMITTER_STATE]?.transmitState;

  const isXentaEmissionUnknown = !xentaTransmitState || xentaTransmitState === XentaTransmitStateEnum.UNKNOWN;
  const isXentaRotationUnknown = !status || status.mastMotion === XentaMastMotionEnum.UNKNOWN;

  function handleEmissionRotation(sendEnableCommand: boolean, isEmissionButton: boolean) {
    if (status !== null) {
      let command: XentaCommand;

      if (isEmissionButton) {
        // Gestion de l'émission
        command = {
          type: XentaCommandEnum.COMMAND_PSR_TX_ON,
          on: sendEnableCommand,
          sensorCode: configuration.code,
        } as XentaEmissionCommand;
      } else if (sendEnableCommand) {
        command = {
          type: XentaCommandEnum.COMMAND_ANTENNA_SURVEILLANCE,
          mode: XentaSurveillanceModeEnum.PROFILE_1,
          sensorCode: configuration.code,
        } as XentaRotationCommand;
      } else {
        command = {
          type: XentaCommandEnum.COMMAND_ANTENNA_STOP_AND_STARE_AZIMUTH,
          sensorCode: configuration.code,
          azimuth: XENTA_INITIAL_AZIMUTH,
        } as XentaStopRotationCommand;
      }
      const toastParams = {
        state: sendEnableCommand,
        commandValues: { state: sendEnableCommand },
        command: `sensors.commands.xenta.${isEmissionButton ? 'emissionCommand' : 'rotationCommand'}`,
        name: configuration.name,
      };
      createSensorToast({
        ...toastParams,
        toast: ToastStatusEnum.INFO,
      });
      postXentaCommand({
        site: configuration.site,
        appCode: configuration.appCode,
        command,
      })
        .unwrap()
        .then(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.SUCCESS,
          }),
        )
        .catch(() =>
          createSensorToast({
            ...toastParams,
            toast: ToastStatusEnum.ERROR,
          }),
        );
    }
  }

  function rotatingState() {
    if (isXentaRotationUnknown) {
      return XentaMastMotionEnum.UNKNOWN;
    }
    return status.mastMotion;
  }

  const actions = [
    { action: 'start', i18nKey: 'sensors.tab.commands.xenta.start' },
    { action: 'stop', i18nKey: 'sensors.tab.commands.xenta.stop' },
  ];

  return (
    <>
      <DataSheetCard
        description={formatMessage({ id: 'sensors.tab.commands.xenta.rotationDescription' })}
        subDescription={formatMessage(
          { id: 'sensors.tab.commands.xenta.subDescription' },
          { state: formatMessage({ id: `sensors.tab.commands.xenta.state.${rotatingState()}` }) },
        )}
        actions={actions}
        isLoading={isLoading}
        isDisabled={isXentaRotationUnknown}
        onClick={(action) => handleEmissionRotation(action === 'start', false)}
      />
      <DataSheetCard
        description={formatMessage({ id: 'sensors.tab.commands.xenta.emissionDescription' })}
        actions={actions}
        isLoading={isLoading}
        isDisabled={isXentaEmissionUnknown}
        onClick={(action) => handleEmissionRotation(action === 'start', true)}
      />
    </>
  );
}
